import type { ChatbotAPI, ChatbotConfig, ChatbotTheme, PostMessageData } from './types';

class ChatbotLoader implements ChatbotAPI {
  private config: ChatbotConfig | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private container: HTMLDivElement | null = null;
  private isIframeLoaded = false;
  private eventHandlers: Map<string, Set<(data: any) => void>> = new Map();
  private messageQueue: PostMessageData[] = [];

  init(config: ChatbotConfig): void {
    if (this.config) {
      console.warn('[CDSChatbot] Already initialized');
      return;
    }

    this.config = {
      position: 'bottom-right',
      locale: 'vi',
      autoOpen: false,
      visibility: {
        showAgentName: true,
        showDescription: true,
        showAvatar: true,
        showQuickQuestions: true,
      },
      ...config,
    };

    this.createContainer();
    this.createIframe();
    this.setupMessageListener();

    if (this.config.autoOpen) {
      setTimeout(() => this.open(), 500);
    }
  }

  private createContainer(): void {
    this.container = document.createElement('div');
    this.container.id = 'cds-chatbot-container';
    this.container.style.cssText = this.getContainerStyles();
    document.body.appendChild(this.container);
  }

  private getContainerStyles(): string {
    const position = this.config?.position || 'bottom-right';
    const positions = {
      'bottom-right': 'bottom: 24px; right: 24px;',
      'bottom-left': 'bottom: 24px; left: 24px;',
      'top-right': 'top: 24px; right: 24px;',
      'top-left': 'top: 24px; left: 24px;',
    };

    return `
      position: fixed;
      ${positions[position]}
      z-index: 999999;
      pointer-events: none;
    `;
  }

  private createIframe(): void {
    if (!this.container || !this.config) return;

    this.iframe = document.createElement('iframe');
    this.iframe.id = 'cds-chatbot-iframe';

    // Security: sandbox iframe
    this.iframe.sandbox.add('allow-scripts', 'allow-same-origin', 'allow-forms', 'allow-popups');
    this.iframe.allow = 'microphone; clipboard-read; clipboard-write;';

    // Get iframe URL - use root index.html in dev
    const iframeUrl = import.meta.env.VITE_APP_URL || window.location.origin;

    this.iframe.src = iframeUrl;

    // console.log('[CDSChatbot] Creating iframe with URL:', iframeUrl);
    this.iframe.style.cssText = `
      border: none;
      width: 72px;
      height: 72px;
      border-radius: 50%;
      box-shadow: none;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      background: transparent;
      pointer-events: auto;
    `;

    this.iframe.onload = () => {
      // console.log('[CDSChatbot] Iframe loaded successfully');
      this.isIframeLoaded = true;
      this.sendMessage({ type: 'INIT', payload: this.config });
      this.flushMessageQueue();
    };

    this.iframe.onerror = error => {
      console.error('[CDSChatbot] Iframe load error:', error);
    };

    this.container.appendChild(this.iframe);
  }

  private setupMessageListener(): void {
    window.addEventListener('message', (event: MessageEvent) => {
      // Security: validate origin if needed
      // if (event.origin !== expectedOrigin) return;

      // console.log('[CDSChatbot] Received postMessage:', event.data);

      // Check if it's our message format
      if (!event.data || typeof event.data !== 'object' || !event.data.type) {
        return;
      }

      const { type, payload } = event.data;

      switch (type) {
        case 'READY':
          // console.log('[CDSChatbot] Handling READY');
          this.emit('ready', payload);
          break;

        case 'RESIZE':
          // console.log('[CDSChatbot] Handling RESIZE:', payload);
          this.handleResize(payload);
          break;

        case 'TOGGLE':
          // console.log('[CDSChatbot] Handling TOGGLE from iframe');
          // Iframe tự handle toggle, parent chỉ cần lắng nghe
          break;

        case 'MAXIMIZE':
          // console.log('[CDSChatbot] Maximizing chatbot');
          this.handleMaximize();
          break;

        case 'MINIMIZE':
          // console.log('[CDSChatbot] Restoring chatbot');
          this.handleMinimize();
          break;

        case 'NEW_MESSAGE':
          // console.log('[CDSChatbot] Handling NEW_MESSAGE');
          this.emit('newMessage', payload);
          break;

        case 'ERROR':
          console.error('[CDSChatbot] Error:', payload);
          this.emit('error', payload);
          break;

        case 'OPEN':
          // console.log('[CDSChatbot] Chat opened');
          this.emit('open', payload);
          break;

        case 'CLOSE':
          // console.log('[CDSChatbot] Chat closed');
          this.emit('close', payload);
          break;
      }
    });
  }

  private handleResize(size: { width: number; height: number; expanded: boolean }): void {
    if (!this.iframe) return;

    if (size.expanded) {
      // Expanded state
      const isMobile = window.innerWidth < 640;
      this.iframe.style.width = isMobile ? '100vw' : '420px';
      this.iframe.style.height = isMobile ? '100vh' : '680px';
      this.iframe.style.borderRadius = isMobile ? '0' : '20px';
      this.iframe.style.maxHeight = '90vh';
      this.iframe.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05)';

      // Adjust container for mobile
      if (isMobile && this.container) {
        this.container.style.inset = '0';
        this.container.style.bottom = '0';
        this.container.style.right = '0';
        this.container.style.left = '0';
        this.container.style.top = '0';
      }
    } else {
      // Collapsed state
      this.iframe.style.width = '72px';
      this.iframe.style.height = '72px';
      this.iframe.style.borderRadius = '50%';
      this.iframe.style.boxShadow = 'none';

      // Reset container position
      if (this.container) {
        this.container.style.cssText = this.getContainerStyles();
      }
    }
  }

  private handleMaximize(): void {
    if (!this.iframe || !this.container) return;

    // Full screen mode
    this.iframe.style.width = '100vw';
    this.iframe.style.height = '100vh';
    this.iframe.style.borderRadius = '0';
    this.iframe.style.maxHeight = '100vh';
    this.iframe.style.boxShadow = 'none';

    this.container.style.inset = '0';
    this.container.style.bottom = '0';
    this.container.style.right = '0';
    this.container.style.left = '0';
    this.container.style.top = '0';
  }

  private handleMinimize(): void {
    if (!this.iframe || !this.container) return;

    // Restore to normal expanded state
    const isMobile = window.innerWidth < 640;
    this.iframe.style.width = isMobile ? '100vw' : '420px';
    this.iframe.style.height = isMobile ? '100vh' : '680px';
    this.iframe.style.borderRadius = isMobile ? '0' : '20px';
    this.iframe.style.maxHeight = '90vh';
    this.iframe.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05)';

    // Reset container position
    if (isMobile) {
      this.container.style.inset = '0';
    } else {
      this.container.style.cssText = this.getContainerStyles();
    }
  }

  private sendMessage(data: PostMessageData): void {
    if (!this.isIframeLoaded || !this.iframe?.contentWindow) {
      this.messageQueue.push(data);
      return;
    }

    this.iframe.contentWindow.postMessage(data, '*');
  }

  private flushMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (message) this.sendMessage(message);
    }
  }

  open(): void {
    // console.log('[CDSChatbot] Opening chatbot');
    this.sendMessage({ type: 'OPEN' });
  }

  close(): void {
    // console.log('[CDSChatbot] Closing chatbot');
    this.sendMessage({ type: 'CLOSE' });
  }

  toggle(): void {
    // console.log('[CDSChatbot] Toggling chatbot');
    this.sendMessage({ type: 'TOGGLE' });
  }

  setTheme(theme: ChatbotTheme): void {
    if (this.config) {
      this.config.theme = { ...this.config.theme, ...theme };
    }
    this.sendMessage({ type: 'SET_THEME', payload: theme });
  }

  on(event: string, callback: (data: any) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(callback);
  }

  off(event: string, callback: (data: any) => void): void {
    this.eventHandlers.get(event)?.delete(callback);
  }

  private emit(event: string, data: any): void {
    this.eventHandlers.get(event)?.forEach(callback => callback(data));
  }

  destroy(): void {
    if (this.container) {
      document.body.removeChild(this.container);
    }
    this.container = null;
    this.iframe = null;
    this.config = null;
    this.isIframeLoaded = false;
    this.eventHandlers.clear();
    this.messageQueue = [];
  }
}

// Singleton instance
const chatbotInstance = new ChatbotLoader();

// Export as global
if (typeof window !== 'undefined') {
  (window as any).CDSChatbot = chatbotInstance;
}

export default chatbotInstance;
